import cn from "mcn";
import { nanoid } from "nanoid";
import { useMemo, useRef } from "react";
import { FaCheck } from "react-icons/fa6";
import { PiMinusBold } from "react-icons/pi";
import type React from "react";
import { forwardRef } from "react";

const variations = {
  unchecked: {
    enabled: cn(
      "bg-surface-1 border-outline hover:bg-neutral-tonal hover:border-outline-variant peer-focus:bg-neutral-tonal peer-focus:border-primary",
    ),
    disabled: cn("bg-neutral-tonal border-neutral-on-disabled"),
  },
  checked: {
    enabled: cn("bg-primary border-primary hover:bg-primary-hover hover:border-primary-hover"),
    disabled: cn("bg-primary-disabled border-neutral-on-disabled text-primary-on-disabled"),
  },
  indeterminate: {
    enabled: cn("bg-primary border-outline hover:bg-primary-hover hover:border-outline-variant"),
    disabled: cn("bg-primary-disabled border-neutral-on-disabled text-primary-on-disabled"),
  },
};

export const Checkbox = forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input"> & {
    label?: string | React.ReactNode;
    variant?: "round" | "square";
  }
>(function Checkbox(props, ref) {
  const { id, checked, disabled, label, variant, className, onChange, ...inputProps } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const checkboxId = useMemo(() => id ?? nanoid(), [id]);

  const checkedClassName = cn(
    "peer-checked:flex peer-disabled:text-primary-on-disabled text-white hidden items-center justify-center w-full h-full",
  );

  const indeterminate = cn(
    "peer-indeterminate:flex peer-disabled:text-primary-on-disabled text-white hidden items-center justify-center w-full h-full",
  );

  const combinedRef = (node: HTMLInputElement) => {
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }
    inputRef.current = node;
  };

  const variation = checked ? variations.checked : variations.unchecked;

  const style = disabled ? variation.disabled : variation.enabled;

  return (
    <div className="flex items-center">
      <div
        className={cn(
          "relative border-1 overflow-hidden w-[16px] h-[16px]",
          style,
          className,
          [variant === "round", "rounded-full", "rounded-md"],
          [
            checked,
            "focus-within:border-primary focus-within:bg-primary-hover",
            "focus-within:border-primary focus-within:bg-neutral-tonal",
          ],
        )}
      >
        <input
          type="checkbox"
          className={cn("peer absolute opacity-0 z-0 w-full h-full", [
            disabled,
            "cursor-not-allowed",
            "cursor-pointer",
          ])}
          {...inputProps}
          checked={checked}
          disabled={disabled}
          onChange={(e) => {
            onChange?.(e);
            if (inputRef.current) {
              inputRef.current.blur();
            }
          }}
          id={checkboxId}
          ref={combinedRef}
        />
        <div className={checkedClassName}>
          <FaCheck />
        </div>
        <div className={indeterminate}>
          <PiMinusBold />
        </div>
      </div>
      {label && (
        <label htmlFor={checkboxId} className="text-md text-foreground ml-2 cursor-pointer">
          {label}
        </label>
      )}
    </div>
  );
});
